import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ExamSectionModal from "./ExamSectionModal";
import { useUserStore } from "../../stores/useUserStore";

// --- helpers: locale title
function pickLocale(obj = {}, lang = "fa") {
  if (!obj || typeof obj !== "object") return "";
  const code = lang?.startsWith("fa") ? "fa" : "en";
  return (
    obj[code] ||
    obj[code === "fa" ? "en" : "fa"] ||
    Object.values(obj).find(Boolean) ||
    ""
  );
}

// --- helpers: draft
const draftKey = (examId) => `attemptDraft:${examId}`;
function readDraft(examId) {
  try {
    const raw = localStorage.getItem(draftKey(examId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}
function clearDraftCompletely(examId) {
  try {
    localStorage.removeItem(draftKey(examId));
    localStorage.removeItem(`attempt:${examId}`); // رفرنس تلاش
  } catch {}
}

// --- Resume modal (وسط صفحه)
const ResumeAskModal = ({ open, onResume, onFresh, onClose }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
        <h3 className="text-lg font-bold mb-2">ادامه یا شروع جدید؟</h3>
        <p className="text-sm text-gray-600 mb-5">
          یک تلاش ذخیره‌شده از این آزمون پیدا شد. می‌خواهید از همان ادامه دهید
          یا از اول شروع کنید؟
        </p>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onFresh}
            className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
          >
            شروع جدید
          </button>
          <button
            onClick={onResume}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            ادامه بده
          </button>
        </div>
      </div>
    </div>
  );
};

const ExamCard = ({ exam, subscriptions = [] }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRtl = i18n.language.includes("fa");

  const user = useUserStore((s) => s.user);
  const organization = user?.organization || user?.org || null;
  const rawScopes = organization?.categoryScopes || user?.categoryScopes || [];

  const normalizeScopeId = (s) => {
    if (!s) return null;
    if (typeof s === "string") return String(s);
    if (s.category) return String(s.category);
    if (s.categoryId) return String(s.categoryId);
    if (s._id) return String(s._id);
    return null;
  };

  const orgCategoryIds = useMemo(
    () =>
      (Array.isArray(rawScopes) ? rawScopes : [])
        .map(normalizeScopeId)
        .filter(Boolean),
    [rawScopes]
  );

  // بازشدن انتخاب سکشن‌ها
  const [sectionModalOpen, setSectionModalOpen] = useState(false);

  // رزومه‌مودال در سطح کارت
  const [resumeOpen, setResumeOpen] = useState(false);
  const [pendingStart, setPendingStart] = useState(null); // { mode, sectionKey }

  const title = useMemo(
    () => pickLocale(exam.title, i18n.language),
    [exam.title, i18n.language]
  );
  const sections = Array.isArray(exam.sections) ? exam.sections : [];
  const sectionCount = sections.length || exam.sectionCount || 0;
  const firstSectionKey = sections[0]?.key || "main";
  const isOrgUser = !!user?.organization;

  // ✅ بررسی اشتراک کاربر
  const userSub = useMemo(() => {
    if (isOrgUser) return null; // کاربر سازمانی → subscription اهمیتی ندارد
    return subscriptions.find((s) => s?.category?._id === exam?.category?._id);
  }, [isOrgUser, subscriptions, exam]);

  // کاربر سازمانی: اگر category آزمون در scope هست → مجاز
  const examCategoryId = exam?.category?._id ? String(exam.category._id) : null;

  const hasOrgAccess =
    isOrgUser && examCategoryId
      ? orgCategoryIds.includes(examCategoryId)
      : false;

  // برای کاربر غیرسازمانی: مثل قبل
  const plan = userSub?.plan;
  const attemptsUsed = userSub?.usage?.attemptsUsed || 0;
  const canUseNonOrg = !!userSub;
  const quotaExceededNonOrg = plan === "free" && attemptsUsed >= 1;
  const orgScopes = (user?.categoryScopes || []).map((c) =>
    typeof c === "string" ? c : c.$oid || c._id || String(c)
  );

  const hasAccessToCategory = orgScopes.includes(examCategoryId);

  let isDisabled = false;

  if (isOrgUser) {
    // کاربر سازمانی → فقط در دسته‌های مجاز می‌تواند آزمون را شروع کند
    isDisabled = !hasAccessToCategory;
  } else {
    // کاربر معمولی → همان منطق اشتراک قبلی
    isDisabled = !canUseNonOrg || quotaExceededNonOrg;
  }

  // کلیک روی کارت
  const handleCardClick = () => {
    if (isDisabled) {
      if (isOrgUser) {
        alert("❌ شما در سازمان خود به این دسته‌بندی دسترسی ندارید.");
      } else {
        if (!canUseNonOrg)
          alert("❌ شما اشتراک فعالی برای این دسته‌بندی ندارید.");
        else if (quotaExceededNonOrg)
          alert("⚠️ سهمیه آزمون رایگان شما تمام شده است.");
      }
      return;
    }
    setSectionModalOpen(true);
  };

  // شروع (از مدال سکشن‌ها می‌آید)
  const handleStart = (mode, sectionKey) => {
    // محدودیت‌ها:
    // - test: فقط از سکشن اول
    // - practice: هر سکشنی مجاز است
    const normalizedSectionKey =
      mode === "test" ? firstSectionKey : sectionKey || firstSectionKey;

    // اگر درفت وجود دارد، قبل از رفتن بپرسیم
    const draft = readDraft(exam._id);
    if (draft) {
      setPendingStart({ mode, sectionKey: normalizedSectionKey });
      setResumeOpen(true);
      return;
    }

    // درفتی نبود → مستقیم برو
    navigate(
      `/exam/preview/${exam._id}?mode=${mode}&section=${encodeURIComponent(
        normalizedSectionKey
      )}`
    );
  };

  // کاربر انتخاب کرد «ادامه بده»
  const onResume = () => {
    setResumeOpen(false);
    if (!pendingStart) return;
    navigate(
      `/exam/preview/${exam._id}?mode=${
        pendingStart.mode
      }&section=${encodeURIComponent(pendingStart.sectionKey)}`
    );
    setPendingStart(null);
  };

  // کاربر انتخاب کرد «شروع جدید»
  const onFresh = () => {
    clearDraftCompletely(exam._id);
    setResumeOpen(false);
    if (!pendingStart) return;
    navigate(
      `/exam/preview/${exam._id}?mode=${
        pendingStart.mode
      }&section=${encodeURIComponent(pendingStart.sectionKey)}`
    );
    setPendingStart(null);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleCardClick}
        className={`w-full text-start rounded-2xl border p-5 transition-all duration-300 ${
          isDisabled
            ? "bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed opacity-80"
            : "bg-white border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5"
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base sm:text-lg font-bold text-gray-800 line-clamp-2">
            {title || t("exam_card.untitled")}
          </h3>
          <span className="shrink-0 text-xs sm:text-sm px-2 py-1 rounded-lg bg-blue-50 text-blue-700">
            {t("exam_card.sections")}: {sectionCount}
          </span>
        </div>

        {isDisabled && (
          <div className="mt-2 text-xs text-red-500 font-medium">
            {isOrgUser
              ? "شما در سازمان‌تان به این دسته‌بندی دسترسی ندارید"
              : quotaExceededNonOrg
              ? "سهمیه آزمون رایگان شما تمام شده"
              : "اشتراک فعال برای این دسته‌بندی ندارید"}
          </div>
        )}
      </button>

      {/* انتخاب سکشن/مود */}
      <ExamSectionModal
        open={sectionModalOpen}
        onClose={() => setSectionModalOpen(false)}
        examTitle={title || t("exam_card.untitled")}
        sections={sections}
        fallbackSectionKey={firstSectionKey}
        onStart={(mode, sectionKey) => handleStart(mode, sectionKey)}
      />

      {/* رزومه‌مودال روی کارت */}
      <ResumeAskModal
        open={resumeOpen}
        onResume={onResume}
        onFresh={onFresh}
        onClose={() => setResumeOpen(false)}
      />
    </>
  );
};

ExamCard.propTypes = {
  exam: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.object,
    sections: PropTypes.array,
    sectionCount: PropTypes.number,
    category: PropTypes.shape({
      _id: PropTypes.string,
      title: PropTypes.string,
    }),
  }).isRequired,
  subscriptions: PropTypes.array,
};

export default ExamCard;
